/**
 * Cleans the Markdown tree of a team message before it is rendered.
 *
 * The team writes on GitHub, where raw HTML is allowed. Here HTML is never
 * rendered: its tags are dropped and only their text is kept, so the user never
 * reads markup. Images hosted on GitHub come from a private repository and
 * would not load: a placeholder takes their place.
 */

/** The slice of an mdast node this module reads and writes. */
interface MarkdownNode {
  type: string
  value?: string
  url?: string
  identifier?: string
  children?: MarkdownNode[]
}

export interface TeamMarkdownOptions {
  /** Text shown in place of an image the user cannot load. */
  imagePlaceholder: string
}

/** Only real HTML tags are dropped: `<nome cognome>` stays as written. */
const HTML_TAGS = new Set(
  (
    'a abbr address area article aside audio b base bdi bdo blockquote body br button canvas ' +
    'caption center cite code col colgroup data datalist dd del details dfn dialog div dl dt em ' +
    'embed fieldset figcaption figure font footer form frame frameset h1 h2 h3 h4 h5 h6 head ' +
    'header hgroup hr html i iframe img input ins kbd label legend li link main map mark menu ' +
    'meta meter nav noscript object ol optgroup option output p param picture pre progress q rp ' +
    'rt ruby s samp script search section select slot small source span strike strong style sub ' +
    'summary sup svg table tbody td template textarea tfoot th thead time title tr track tt u ul ' +
    'var video wbr'
  ).split(' '),
)

const COMMENT = /<!--[\s\S]*?-->/g
const DECLARATION = /<![a-z[][\s\S]*?>|<\?[\s\S]*?\?>/gi
const UNSEEN_ELEMENT = /<(script|style)\b[\s\S]*?<\/\1\s*>/gi
const TAG = /<\/?([a-z][a-z0-9]*)\b(?:"[^"]*"|'[^']*'|[^'">])*>/gi
const ENTITY = /&(amp|lt|gt|quot|apos|nbsp|#39);/g
const ENTITY_TEXT: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  '#39': "'",
}

/** Containers whose children are blocks: text in them needs a paragraph. */
const BLOCK_PARENTS = new Set(['root', 'blockquote', 'listItem', 'footnoteDefinition'])

/** The text left by a piece of raw HTML once its tags are gone. */
function htmlToText(html: string, imagePlaceholder: string): string {
  return html
    .replace(COMMENT, '')
    .replace(DECLARATION, '')
    .replace(UNSEEN_ELEMENT, '')
    .replace(TAG, (tag: string, name: string) => {
      const element = name.toLowerCase()
      if (!HTML_TAGS.has(element)) return tag
      if (element === 'img') return imagePlaceholder
      return element === 'br' ? '\n' : ''
    })
    .replace(ENTITY, (_entity: string, name: string) => ENTITY_TEXT[name])
}

/** True for the uploads of a GitHub comment, private like the repository. */
export function isGithubAttachment(url: string | undefined): boolean {
  if (!url) return false
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  const host = parsed.hostname.toLowerCase()
  return (
    (host === 'github.com' && parsed.pathname.startsWith('/user-attachments/')) ||
    host === 'private-user-images.githubusercontent.com'
  )
}

function text(value: string): MarkdownNode {
  return { type: 'text', value }
}

function htmlReplacement(
  node: MarkdownNode,
  parent: MarkdownNode,
  imagePlaceholder: string,
): MarkdownNode[] {
  const content = htmlToText(node.value ?? '', imagePlaceholder)
  if (!BLOCK_PARENTS.has(parent.type)) return content ? [text(content)] : []
  return content
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => ({ type: 'paragraph', children: [text(block)] }))
}

function collectDefinitions(node: MarkdownNode, urls: Map<string, string>): void {
  if (node.type === 'definition' && node.identifier && node.url) {
    urls.set(node.identifier, node.url)
  }
  node.children?.forEach((child) => collectDefinitions(child, urls))
}

/** remark plugin for the messages of the team. */
export function remarkTeamMarkdown({ imagePlaceholder }: TeamMarkdownOptions) {
  return (tree: MarkdownNode): void => {
    const definitions = new Map<string, string>()
    collectDefinitions(tree, definitions)

    const clean = (parent: MarkdownNode): void => {
      if (!parent.children) return
      parent.children = parent.children.flatMap((node) => {
        if (node.type === 'html') return htmlReplacement(node, parent, imagePlaceholder)
        const imageUrl =
          node.type === 'image'
            ? node.url
            : node.type === 'imageReference'
              ? definitions.get(node.identifier ?? '')
              : undefined
        if (isGithubAttachment(imageUrl)) return [text(imagePlaceholder)]
        clean(node)
        return [node]
      })
    }
    clean(tree)
  }
}
